/**
 * Board / bono helpers shared by the admin editors and the buyer flow.
 * Mirrors backend/utils/boardPlans.js (the server always recomputes prices and counts).
 *
 * Board modes (rifa.config.boardMode):
 *  - 'separate' (default): each category is its own grid.
 *  - 'shared': one grid (config.sharedBoard); categories are plans chosen per square at checkout.
 *    Ticket pool = filas × columnas × max(ticketsPorCasilla across plans).
 */

/** Pseudo category used to render a shared board through the per-category grid components. */
export const SHARED_BOARD_KEY = '__shared_board__';

export const PICKER_MODES = ['both', 'manual', 'random'];

export const isSharedBoard = rifa => rifa?.tipo === 'board' && rifa?.config?.boardMode === 'shared';

/** Draws are on unless explicitly disabled (older raffles have no field). */
export const sorteosEnabledOf = rifa => rifa?.sorteosEnabled !== false;

export const pickerModeOf = rifa =>
  mostrarNumerosOf(rifa)
    ? PICKER_MODES.includes(rifa?.pickerMode)
      ? rifa.pickerMode
      : 'both'
    : 'random';

/** Buyers see / choose their numbers unless explicitly disabled (missing = true). */
export function mostrarNumerosOf(rifa) {
  return rifa?.mostrarNumeros !== false;
}

/** Numbers per square: integer ≥ 0 (0 = bono without numbers). Missing → 1. */
export function toTicketsPerSquare(value) {
  if (value === undefined || value === null || value === '') return 1;
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.floor(n));
}

export function sharedBoardSize(config) {
  return {
    filas: Math.max(1, Math.floor(Number(config?.sharedBoard?.filas) || 1)),
    columnas: Math.max(1, Math.floor(Number(config?.sharedBoard?.columnas) || 1)),
  };
}

/** Same rule as the server: separate = Σ grid × tickets, shared = grid × max tickets. */
export function computeBoardTotalTickets(config) {
  const categories = config?.categories || [];
  if (config?.boardMode === 'shared') {
    const { filas, columnas } = sharedBoardSize(config);
    const maxPer = categories.reduce(
      (m, cat) => Math.max(m, toTicketsPerSquare(cat.ticketsPorCasilla)),
      0
    );
    return filas * columnas * maxPer;
  }
  return categories.reduce(
    (sum, cat) =>
      sum +
      (Number(cat.filas) || 0) *
        (Number(cat.columnas) || 0) *
        toTicketsPerSquare(cat.ticketsPorCasilla),
    0
  );
}

/**
 * Shared board as one pseudo category so grid components keyed by `categoria` keep working.
 * The plan chosen by the buyer stays available as `planCategoria` / `planColor`.
 */
export function toSharedGridView(rifa, casillas, label = '') {
  const { filas, columnas } = sharedBoardSize(rifa?.config);
  return {
    categories: [{ nombre: SHARED_BOARD_KEY, label, color: '', filas, columnas }],
    casillas: (casillas || []).map(c => ({
      ...c,
      categoria: SHARED_BOARD_KEY,
      planCategoria: c.categoria || '',
      planColor: c.color || '',
    })),
  };
}
